import type { PaymentMethodOption } from '../interfaces/InvoiceService';

/**
 * Adapter for the `GET /payment_methods` wire array (Phase 5.3, D-11).
 *
 * Wire-contract sources (read 2026-06-04, port research §"GET /payment_methods"):
 *   - ../api/app/models/payment_methods.rb:10-117, :36-93 (per-key shape)
 *   - ../api/app/controllers/v2/payment_methods_controller.rb:8,47-51,76-78
 *     (plain JSON array — NOT a jbuilder; customer filter strips
 *     NON_PROCESSOR_PAYMENT_METHODS so check/cash/external never reach us)
 *
 * The endpoint returns a plain JSON array of per-key method descriptors. Each
 * entry's `key` discriminates the gateway + transaction type and which SDK creds
 * it carries. The adapter normalizes the array into the domain
 * `PaymentMethodOption[]` the pay modal renders, mapping per-key creds and
 * collapsing to AT MOST one card + one ACH (D-05).
 *
 * Field-by-field map (wire key → domain field):
 *
 *   | wire `key`     | gateway        | transactionType | creds                       |
 *   | -------------- | -------------- | --------------- | --------------------------- |
 *   | stax_card      | stax           | card            | webId (web_id)              |
 *   | gpi_card       | globalpayments | card            | tokenizationKey + environment|
 *   | stax_ach       | stax           | ach             | webId (web_id)              |
 *   | gpi_ach        | globalpayments | ach             | (no extra creds)            |
 *   | payment_method | (on-file)      | payment_method  | label only                  |
 *
 * Stax-wins precedence (D-05): a tenant should be configured with a single
 * processor, but the customer-filtered list can in principle carry both a Stax
 * and a GPI variant for the same transaction type. The adapter keeps the FIRST
 * method seen per transaction type and, when both are present, prefers Stax — so
 * the modal always renders exactly one card and one ACH option. The adapter does
 * not re-derive the gateway from tenant config; it trusts the per-key wire.
 *
 * `gateway` is carried for SDK selection ONLY and is never surfaced to the
 * customer (threat T-05.3-04). Optional creds are omit-vs-preserve.
 */

/** Snake_case mirror of one entry in the `GET /payment_methods` wire array. */
export interface PaymentMethodWire {
  key: string;
  transaction_type?: string | null;
  label?: string | null;
  web_id?: string | null;
  tokenization_key?: string | null;
  environment?: string | null;
  card_savable?: boolean | null;
  [extra: string]: unknown;
}

/** Resolve the SDK-selection gateway discriminator from the wire `key`. */
function gatewayFromKey(key: string): PaymentMethodOption['gateway'] | null {
  if (key.startsWith('stax')) return 'stax';
  if (key.startsWith('gpi')) return 'globalpayments';
  return null;
}

/**
 * Normalize the wire `transaction_type` into the domain union. Preserves the
 * server value verbatim when it is one of the known kinds; otherwise infers from
 * the `key` only when the key UNAMBIGUOUSLY encodes the type. Returns `null` for
 * a genuinely unrecognized entry.
 *
 * WR-06: previously this defaulted an unknown type to `'card'`. Coupled with
 * `gatewayFromKey`, a malformed/new wire entry (e.g. a future `stax_wallet`)
 * would be coerced into a card method carrying whatever creds it had, mounting a
 * card gateway form against a non-card context and posting `transaction_type:
 * 'card'` for something that isn't a card. Silently MISLABELING a method on the
 * payment path is worse than dropping it, so an unrecognized type now yields
 * `null` (excluded) — matching the unknown-gateway path which already returns
 * null. The `key`-based inference is retained only where the key clearly encodes
 * the type (`payment_method`, `*ach`, `*card`).
 */
function transactionTypeOf(
  wire: PaymentMethodWire,
): PaymentMethodOption['transactionType'] | null {
  const tt = wire.transaction_type;
  if (tt === 'card' || tt === 'ach' || tt === 'payment_method') return tt;
  if (wire.key === 'payment_method') return 'payment_method';
  if (wire.key.endsWith('ach')) return 'ach';
  if (wire.key.endsWith('card')) return 'card';
  return null;
}

/** Attach the per-key SDK creds onto a card/ACH option (omit-vs-preserve). */
function attachCreds(
  option: PaymentMethodOption,
  wire: PaymentMethodWire,
): void {
  if (wire.web_id != null) option.webId = wire.web_id;
  if (wire.tokenization_key != null) {
    option.tokenizationKey = wire.tokenization_key;
  }
  if (wire.environment === 'prod' || wire.environment === 'test') {
    option.environment = wire.environment;
  }
  if (typeof wire.card_savable === 'boolean') {
    option.cardSavable = wire.card_savable;
  }
}

/**
 * Map one wire entry to a domain option, or null when the transaction type or
 * gateway is unrecognized (WR-06: exclude rather than mislabel as card).
 */
function adaptOne(wire: PaymentMethodWire): PaymentMethodOption | null {
  const transactionType = transactionTypeOf(wire);
  if (!transactionType) return null;

  // On-file payment_method has no gateway/creds — carry label only.
  if (transactionType === 'payment_method') {
    return {
      gateway: 'stax',
      transactionType,
      label: wire.label ?? 'Saved payment method',
    };
  }

  const gateway = gatewayFromKey(wire.key);
  if (!gateway) return null;

  const option: PaymentMethodOption = {
    gateway,
    transactionType,
    label: wire.label ?? (transactionType === 'ach' ? 'ACH' : 'Credit Card'),
  };
  attachCreds(option, wire);
  return option;
}

/** True when `candidate` should replace `existing` for the same txn type. */
function staxWins(
  existing: PaymentMethodOption,
  candidate: PaymentMethodOption,
): boolean {
  return existing.gateway !== 'stax' && candidate.gateway === 'stax';
}

/**
 * Pure adapter: maps the `GET /payment_methods` wire array to the domain
 * `PaymentMethodOption[]`, collapsing to at most one method per transaction type
 * with Stax-wins precedence (D-05). Order of first appearance is preserved.
 */
export function adaptPaymentMethods(
  wire: PaymentMethodWire[],
): PaymentMethodOption[] {
  const byType = new Map<
    PaymentMethodOption['transactionType'],
    PaymentMethodOption
  >();
  for (const entry of wire) {
    const option = adaptOne(entry);
    if (!option) continue;
    const existing = byType.get(option.transactionType);
    if (!existing || staxWins(existing, option)) {
      byType.set(option.transactionType, option);
    }
  }
  return [...byType.values()];
}
